import { type } from 'arktype';
import { createPaginatedResponseSchema } from '../shared/pagination';

/**
 * Los diez enumerados de identidad y roles (documento 02a, §5.3). "SystemRole" se declara aquí
 * desde la oleada 5 (CL-6, `decisiones.md` §5): `updateUserRoleSchema` necesita validarlo para
 * poder promover/degradar administradores — antes solo se usaba como literal inline en
 * `meResponseSchema`.
 * Cada uno se exporta como constante reutilizable (el array `as const`) y como unión de
 * literales ArkType (`type.enumerated(...)`).
 */

export const USER_TIERS = ['PENDING', 'GRUMETE', 'STANDARD', 'LITE', 'LEGACY', 'COLLABORATOR'] as const;
export const userTierSchema = type.enumerated(...USER_TIERS);
export type UserTier = typeof userTierSchema.infer;

export const FUNNEL_STATES = ['LEAD', 'VERIFIED', 'MEMBER', 'EXPERIENCE', 'ADVOCATE'] as const;
export const funnelStateSchema = type.enumerated(...FUNNEL_STATES);
export type FunnelState = typeof funnelStateSchema.infer;

export const COLLABORATOR_SUBROLES = ['COORDINATOR', 'AMBASSADOR', 'MODERATOR'] as const;
export const collaboratorSubroleSchema = type.enumerated(...COLLABORATOR_SUBROLES);
export type CollaboratorSubrole = typeof collaboratorSubroleSchema.infer;

export const ARCHETYPES = ['DIVERGENTE', 'EXPLORANDOME', 'FITWILD', 'YIN', 'WANDERLUST', 'FERNWEH'] as const;
export const archetypeSchema = type.enumerated(...ARCHETYPES);
export type Archetype = typeof archetypeSchema.infer;

export const AVATAR_TYPES = [
	'AVATAR_1_CONEXION',
	'AVATAR_2_JOVEN',
	'AVATAR_3_PROFESIONAL',
	'AVATAR_4_COORDINADOR',
	'AVATAR_5_PADRE_AVENTUREROS',
] as const;
export const avatarTypeSchema = type.enumerated(...AVATAR_TYPES);
export type AvatarType = typeof avatarTypeSchema.infer;

// Solapadas a propósito (R_18_30 con R_18_23 y R_23_30): decisión del cliente, ver §5.3 y
// §15 D-11 del documento 02a.
export const AGE_RANGES = [
	'R_18_23',
	'R_23_30',
	'R_18_30',
	'R_23_35',
	'R_30_35',
	'R_35_40',
	'R_40_50',
	'R_50_60',
	'R_60_PLUS',
] as const;
export const ageRangeSchema = type.enumerated(...AGE_RANGES);
export type AgeRange = typeof ageRangeSchema.infer;

export const COMM_CHANNELS = ['WHATSAPP', 'EMAIL', 'APP', 'ALL'] as const;
export const commChannelSchema = type.enumerated(...COMM_CHANNELS);
export type CommChannel = typeof commChannelSchema.infer;

export const COMM_FREQUENCIES = ['HIGH', 'MEDIUM', 'LOW', 'NONE'] as const;
export const commFrequencySchema = type.enumerated(...COMM_FREQUENCIES);
export type CommFrequency = typeof commFrequencySchema.infer;

export const CONNECTION_TYPES = ['FRIEND', 'BLOCKED'] as const;
export const connectionTypeSchema = type.enumerated(...CONNECTION_TYPES);
export type ConnectionType = typeof connectionTypeSchema.infer;

export const SYSTEM_ROLES = ['USER', 'ADMIN'] as const;
export const systemRoleSchema = type.enumerated(...SYSTEM_ROLES);
export type SystemRole = typeof systemRoleSchema.infer;

/**
 * Contrato literal de `GET /me` (documento 02a, §6.1): el endpoint más importante del
 * sistema, con efecto lateral deliberado (crea o actualiza la fila de `user`, ver las
 * cuatro reglas del puente en el propio §6.1). `fullName`, `avatarUrl` y
 * `collaboratorSubrole` son opcionales porque pueden no existir todavía (sin foto de
 * Google, o sin ser colaborador).
 */
export const meResponseSchema = type({
	'avatarUrl?': 'string',
	// Documento 04 (§7): si esta persona puede repartir enlaces de organizador. Opcional a
	// propósito (decisión 7 de la tarea de cimientos de la iteración 04): quien lo consuma
	// compara con `=== true`, así que ausente significa "sin permiso" (falla cerrado).
	// Obligatorio habría roto los fixtures de `MeResponse` que ya reparte el repositorio.
	'canInviteOrganizers?': 'boolean',
	'collaboratorSubrole?': collaboratorSubroleSchema,
	email: 'string.email',
	'fullName?': 'string',
	funnelState: funnelStateSchema,
	id: 'string.uuid',
	// Calculado antes de escribir en el alta: le dice al cliente si es el primer acceso.
	isNew: 'boolean',
	// El sello ⚓ de la comunidad histórica (columna `"user"."isOldSchool"`, migración del censo
	// de NocoDB — `supabase/README.md` § Importación del censo histórico): 1.829 personas lo
	// llevan desde esa importación. Existe en la base desde entonces, pero hasta ahora ningún
	// mapper lo leía, así que nunca salía en ninguna respuesta y el sello no se veía en ningún
	// sitio. Opcional, mismo criterio que `canInviteOrganizers`: quien lo consuma compara con
	// `=== true`, así que ausente equivale a "no lo lleva".
	'isOldSchool?': 'boolean',
	// Documento 02c: hasta cuándo vale la matrícula y el precio bloqueado. Solo administración
	// los edita (`updateUserRoleSchema`); ausentes si nunca se ha comprado una matrícula.
	'membershipPriceCents?': 'number',
	'membershipValidUntil?': 'string.date.iso',
	systemRole: "'USER' | 'ADMIN'",
	tier: userTierSchema,
}).onUndeclaredKey('delete');

export type MeResponse = typeof meResponseSchema.infer;

/**
 * Perfil público de otra persona, ya redactado según quién pregunta (§6.3 `GET /users/:id`,
 * §8.3 del documento 02b). El backend construye esta forma aplicando
 * `redactarPorVisibilidad` + `MAPA_VISIBILIDAD_PERFIL_USUARIO` (`rules/visibility.ts`); por
 * eso todo campo salvo `id` es opcional aquí: un campo ausente significa que ese visor no
 * tiene permiso para verlo, nunca que el dato no existe.
 */
export const userProfileResponseSchema = type({
	'adnHexPrimary?': 'string | null',
	'adnHexSecondary?': 'string | null',
	'archetype?': archetypeSchema.or('null'),
	'avatarUrl?': 'string | null',
	'bio?': 'string | null',
	'email?': 'string.email',
	'fullName?': 'string | null',
	id: 'string.uuid',
	'instagram?': 'string | null',
	// El sello ⚓ de la comunidad histórica (ver el JSDoc de `isOldSchool` en `meResponseSchema`,
	// más arriba). Sin `| null`, mismo criterio que `email` en este DTO: la columna en base no es
	// nullable, así que "tiene permiso de verlo" y "el dato existe" son la misma cosa — ausente
	// solo significa "este visor no tiene permiso".
	'isOldSchool?': 'boolean',
	// Documento 02c: mismo criterio de redacción que el resto de este DTO — ausente si quien
	// pregunta no tiene permiso de verlo, `null` si tiene permiso pero no hay matrícula.
	'membershipPriceCents?': 'number | null',
	'membershipValidUntil?': 'string.date.iso | null',
	'username?': 'string | null',
	'whatsapp?': 'string | null',
}).onUndeclaredKey('delete');

export type UserProfileResponse = typeof userProfileResponseSchema.infer;

/**
 * `PATCH /me` (§6.3, endpoint ausente que hace falta): nombre, biografía, foto, contacto,
 * franja de edad, zona, radio, alias y visibilidad del perfil. Se escribe a mano, no con
 * `.partial()`: ninguno de estos campos tiene valor por defecto en `createXxxSchema`, así
 * que aquí no aplica la lección de docs/lessons.md, pero se mantiene la misma disciplina de
 * declarar el PATCH explícitamente.
 */
export const updateMeSchema = type({
	'ageRange?': ageRangeSchema,
	'avatarUrl?': 'string',
	'bio?': 'string',
	'fullName?': 'string',
	'instagram?': 'string',
	'locationId?': 'string.uuid',
	'profilePublic?': 'boolean',
	'radiusKm?': 'number.integer >= 0',
	'username?': 'string',
	'whatsapp?': 'string',
}).onUndeclaredKey('delete');

export type UpdateMe = typeof updateMeSchema.infer;

/**
 * `PATCH /users/:id/role` (§6.3, endpoint ausente que hace falta): que un administrador
 * cambie el tier o conceda permisos. Hoy solo se puede con SQL directo.
 *
 * `systemRole` es alcance añadido sobre el PDF: el documento 02a §6.3 describe este endpoint
 * solo como "cambiar el tier o conceder permisos", sin tocar el rol de sistema. Se amplía por
 * decisión del cliente (propuesta CL-6, `decisiones.md` §5, ya autorizada): sin poder
 * promover/degradar administradores desde este contrato, la guarda de negocio
 * `UsersService.garantizarQuedaAlMenosUnAdmin` —que impide dejar el sistema sin ningún
 * administrador— no tenía ninguna llamada real capaz de dispararla, aunque ya estuviera escrita
 * y probada.
 */
export const updateUserRoleSchema = type({
	'canInviteOrganizers?': 'boolean',
	'collaboratorSubrole?': collaboratorSubroleSchema,
	// Documento 02c: la matrícula se edita a mano desde administración. `null` explícito borra
	// el valor (p. ej. anular una matrícula) — distinto de no enviar la clave, que no cambia nada.
	'membershipPriceCents?': 'number.integer >= 0 | null',
	'membershipValidUntil?': 'string.date.iso | null',
	'systemRole?': systemRoleSchema,
	'tier?': userTierSchema,
}).onUndeclaredKey('delete');

export type UpdateUserRole = typeof updateUserRoleSchema.infer;

/**
 * `GET /users/search` (documento 04 §7.1): el resultado del buscador de personas del
 * formulario de reparto de enlaces. Exactamente `{ id, username?, fullName? }` — la ausencia
 * de `email`, `whatsapp` e `instagram` es el criterio de aceptación CA-04.11 ("buscar gente
 * para invitar nunca expone ni filtra por su contacto"), no un olvido: compárese con
 * `userProfileResponseSchema`, que sí los lleva (redactados por visibilidad) porque responde
 * a una pregunta distinta ("¿qué puede ver ESTE visor de ESTE perfil?"), no a un listado de
 * resultados de búsqueda. `username`/`fullName` opcionales porque cualquiera de los dos puede
 * faltar (documento 02a: ninguno es obligatorio en el perfil).
 */
export const userSearchResponseSchema = type({
	'fullName?': 'string | null',
	id: 'string.uuid',
	'username?': 'string | null',
}).onUndeclaredKey('delete');

export type UserSearchResponse = typeof userSearchResponseSchema.infer;

export const userSearchPaginatedResponseSchema = createPaginatedResponseSchema(userSearchResponseSchema);
export type UserSearchPaginatedResponse = typeof userSearchPaginatedResponseSchema.infer;

/**
 * `POST /me/deletion-request` (respuestas del cliente 2026-09-17, P-18): «Sí, las dos vías: la
 * persona la pide desde su ficha, y administración la ejecuta desde el centro de mando». Esta es
 * la primera vía — la propia persona. Sin cuerpo propio hoy: el identificador sale de la sesión
 * (RN, mismo criterio que `publishExperienceSchema` de `contracts/experiences` — nunca del
 * cuerpo), declarado igualmente como contrato para que un futuro campo opcional no rompa la
 * convención de "cada operación tiene su contrato".
 */
export const requestAccountDeletionSchema = type({}).onUndeclaredKey('delete');

export type RequestAccountDeletion = typeof requestAccountDeletionSchema.infer;

/**
 * `POST /users/:id/deletion` (P-18): la segunda vía — administración EJECUTA la baja desde el
 * centro de mando. El identificador de la persona va en la URL, mismo criterio que
 * `requestAccountDeletionSchema`. «Una baja no es un borrado instantáneo de todo: se marca, se
 * avisa [...] y se ejecuta» — qué hacer con los planes futuros, las plazas confirmadas y los
 * enlaces de esa persona es trabajo del servicio que ejecuta la baja, no forma de este contrato.
 */
export const executeAccountDeletionSchema = type({}).onUndeclaredKey('delete');

export type ExecuteAccountDeletion = typeof executeAccountDeletionSchema.infer;
